/** @format */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import Header from '../../components/common/Header';
// lazy load footer
const Footer = React.lazy(() => import('../../components/common/Footer'));
import axios from 'axios';
import { useAuthStore } from '../../context/useAuthStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface Blog {
	_id: string;
	title: string;
	excerpt: string;
	image: string;
	date: string;
	slug: string;
	count: number;
}

const Blogs: React.FC = () => {
	const { token, clearSession } = useAuthStore();
	const [allBlogs, setAllBlogs] = useState<Blog[]>([]);

	useEffect(() => {
		const fetchBlogs = async () => {
			try {
				const res = await axios.get(`${API_BASE_URL}/allblog`, {
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					withCredentials: true,
				});

				if (res.data?.success && Array.isArray(res.data.success)) {
					const sortedBlogs = (res.data.success as Blog[]).sort((a, b) => {
						return new Date(b.date).getTime() - new Date(a.date).getTime();
					});
					setAllBlogs(sortedBlogs);
				} else {
					setAllBlogs([]);
				}
			} catch (error) {
				if (axios.isAxiosError(error)) {
					const errorMessage =
						error.response?.data?.message || 'Failed to fetch subjects';
					if (
						errorMessage.includes('Session expired') ||
						error.response?.status === 401
					) {
						clearSession('expired');
					}
				}
				setAllBlogs([]);
			}
		};

		fetchBlogs();
	}, [token]);

	return (
		<body className='bg-white p-0! overflow-x-hidden'>
			<Header />

			<main className='min-h-screen py-20 px-4 bg-[#F6FBFD] scroll-mt-24'>
				{/* Page Heading */}
				<div className='text-center max-w-3xl mx-auto mb-14'>
					<h1 className='text-3xl md:text-4xl font-bold text-myButtonBg capitalize'>
						Blogs & Insights
					</h1>
					<p className='text-gray-700 mt-4 text-lg'>
						Exam-focused articles, strategies, and simplified legal concepts
						designed specifically for Indian law students.
					</p>
				</div>

				{/* Blog Grid */}
				<section className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-7xl mx-auto'>
					{allBlogs.map((blog, index) => (
						<div
							key={blog._id || index}
							className='bg-myDark text-myLight rounded-4xl p-2 pb-6 flex flex-col'>
							{/* Image */}
							<div className='flex justify-center'>
								<img
									src={(() => {
										if (!blog.image) return '/what-we-do.png';
										const filename = blog.image.split('/').pop();
										return `https://www.llb360.com/uploads/blogs/${filename}`;
									})()}
									alt={blog.title}
									className='w-full rounded-3xl shadow-lg object-cover object-top aspect-3/4 h-48! sm:h-56!'
								/>
							</div>

							{/* Content */}
							<div className='px-2 flex flex-col flex-grow'>
								<Link
									to={`/blogs/${blog.slug}`}
									className='mt-2 text-sm font-medium underline'>
									<h3 className='text-lg font-semibold mt-2'>{blog.title}</h3>
								</Link>
								<p className='text-myInputBorder mt-2 text-sm flex-grow line-clamp-3'>
									{blog.excerpt}
								</p>
								<div className='flex justify-between items-center pt-4 px-2'>
									<span className='text-xs text-myInputBorder/75'>
										{blog.date}
									</span>
									<span className='text-xs flex gap-1 opacity-75'>
										<FaEye
											color='#cbd5e1'
											size={16}
										/>
										{blog.count}
									</span>
								</div>
							</div>
						</div>
					))}
				</section>
			</main>

			<React.Suspense
				fallback={<div className='h-20 bg-gray-100 animate-pulse' />}>
				<Footer />
			</React.Suspense>
		</body>
	);
};

export default Blogs;
